import { addDays, format, getDay, startOfWeek } from 'date-fns';
import type { Class } from '../../types/class';
import type {
  DayShiftEntry,
  DayShiftField,
  EmployeeWeeklySchedule,
  ScheduleRoom,
  ShiftAssignment,
  StaffMember,
  Weekday,
} from './types';
import {
  DEFAULT_DAY_SHIFT,
  CLOSING_DEFAULT_END,
  CLOSING_DEFAULT_START,
  OPENING_DEFAULT_START,
  FALLBACK_BLOCK_COLOR,
  JS_DAY_TO_WEEKDAY,
  STAFF_AVATAR_PALETTE,
  STAFF_BLOCK_PALETTE,
  WEEK_STARTS_ON,
  WORK_WEEKDAYS,
} from './schedulingConfig';
import { isShiftEducatorGroupe } from '../../services/hr/employeeGroupeService';
import { defaultEndFromStart } from './shiftTimeUtils';

type TeacherRef = { id: string; firstName: string; lastName: string; email?: string };

export function getInitials(firstName: string, lastName: string): string {
  return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
}

export function paletteColor<T extends readonly string[]>(palette: T, index: number): string {
  return palette[index % palette.length] ?? palette[0];
}

export function teachersToStaff(
  teachers: TeacherRef[],
  defaultRoleLabel: string
): StaffMember[] {
  return teachers.map((teacher, index) => ({
    id: teacher.id,
    name: `${teacher.firstName} ${teacher.lastName}`.trim(),
    role: defaultRoleLabel,
    initials: getInitials(teacher.firstName, teacher.lastName),
    colorClass: paletteColor(STAFF_AVATAR_PALETTE, index),
    blockColorClass: paletteColor(STAFF_BLOCK_PALETTE, index),
    email: teacher.email,
  }));
}

/** Strip accents for junk-name checks (été → ete). */
function normalizeClassNameKey(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Demo / leftover names that must never appear as shift Class IDs. */
const EXCLUDED_CLASS_ROOM_NAMES = new Set([
  'ete',
  'summer',
  'pouponniere',
  'les renards',
  'les hiboux',
  'flottant',
]);

export function isShiftClassRoomEligible(cls: Pick<Class, 'name' | 'groupCode'>): boolean {
  if (!isShiftEducatorGroupe(cls.groupCode)) return false;
  const nameKey = normalizeClassNameKey(cls.name ?? '');
  if (!nameKey || EXCLUDED_CLASS_ROOM_NAMES.has(nameKey)) return false;
  return true;
}

/** Rooms for shift UI — educator Class IDs A–F only (no été / Kitchen / name-only rows). */
export function classesToRooms(classes: Class[]): ScheduleRoom[] {
  return [...classes]
    .filter(isShiftClassRoomEligible)
    .sort((a, b) => {
      const ga = (a.groupCode ?? '').trim().toUpperCase();
      const gb = (b.groupCode ?? '').trim().toUpperCase();
      if (ga !== gb) return ga.localeCompare(gb, 'en');
      return a.name.localeCompare(b.name, 'fr');
    })
    .map((cls) => {
      const code = (cls.groupCode ?? '').trim().toUpperCase();
      return {
        id: cls.id,
        name: cls.name,
        subtitle: code,
        color: cls.color,
        letter: code.charAt(0),
      };
    });
}

export function getSeedRooms(): ScheduleRoom[] {
  return [];
}

export function createDefaultDayEntry(): DayShiftEntry {
  return { ...DEFAULT_DAY_SHIFT, roomIds: [] };
}

/** Manager-marked off day — distinct from null (calendar-synced off). */
export function createManualOffDayEntry(): DayShiftEntry {
  return { ...DEFAULT_DAY_SHIFT, roomIds: [], isOff: true };
}

export function isManualOffEntry(entry: DayShiftEntry | null): boolean {
  return Boolean(entry?.isOff);
}

export function createEmptySchedule(employeeId: string): EmployeeWeeklySchedule {
  const days = {} as Record<Weekday, DayShiftEntry | null>;
  for (const weekday of WORK_WEEKDAYS) {
    days[weekday] = null;
  }
  return { employeeId, days, notes: '' };
}

export function updateEmployeeNotes(
  schedules: EmployeeWeeklySchedule[],
  employeeId: string,
  notes: string
): EmployeeWeeklySchedule[] {
  return schedules.map((schedule) =>
    schedule.employeeId === employeeId ? { ...schedule, notes } : schedule
  );
}

export function createEmptyWeeklySchedules(staff: StaffMember[]): EmployeeWeeklySchedule[] {
  return staff.map((member) => createEmptySchedule(member.id));
}

export function pruneSchedulesToStaff(
  schedules: EmployeeWeeklySchedule[],
  staff: StaffMember[]
): EmployeeWeeklySchedule[] {
  const activeIds = new Set(staff.map((member) => member.id));
  return schedules.filter((schedule) => activeIds.has(schedule.employeeId));
}

export function alignSchedulesWithStaff(
  schedules: EmployeeWeeklySchedule[],
  staff: StaffMember[]
): EmployeeWeeklySchedule[] {
  const pruned = pruneSchedulesToStaff(schedules, staff);
  const byEmployee = new Map(pruned.map((s) => [s.employeeId, s]));
  return staff.map((member) => byEmployee.get(member.id) ?? createEmptySchedule(member.id));
}

export function updateScheduleDays(
  schedules: EmployeeWeeklySchedule[],
  employeeId: string,
  day: Weekday,
  updater: (entry: DayShiftEntry | null) => DayShiftEntry | null
): EmployeeWeeklySchedule[] {
  return schedules.map((schedule) => {
    if (schedule.employeeId !== employeeId) return schedule;
    return {
      ...schedule,
      days: {
        ...schedule.days,
        [day]: updater(schedule.days[day] ?? null),
      },
    };
  });
}

export function isDayOff(entry: DayShiftEntry | null): boolean {
  if (!entry) return true;
  return !!entry.isOff;
}

export function cloneDayEntry(entry: DayShiftEntry | null): DayShiftEntry | null {
  if (!entry) return null;
  return {
    ...entry,
    roomIds: [...entry.roomIds],
  };
}

export function swapEmployeeDaySchedules(
  schedules: EmployeeWeeklySchedule[],
  employeeIdA: string,
  employeeIdB: string,
  day: Weekday
): EmployeeWeeklySchedule[] {
  if (employeeIdA === employeeIdB) return schedules;

  const scheduleA = schedules.find((s) => s.employeeId === employeeIdA);
  const scheduleB = schedules.find((s) => s.employeeId === employeeIdB);
  if (!scheduleA || !scheduleB) return schedules;

  const dayFromA = cloneDayEntry(scheduleA.days[day] ?? null);
  const dayFromB = cloneDayEntry(scheduleB.days[day] ?? null);

  return schedules.map((schedule) => {
    if (schedule.employeeId === employeeIdA) {
      return {
        ...schedule,
        days: { ...schedule.days, [day]: dayFromB },
      };
    }
    if (schedule.employeeId === employeeIdB) {
      return {
        ...schedule,
        days: { ...schedule.days, [day]: dayFromA },
      };
    }
    return schedule;
  });
}

export function applyMondayToWeekdays(schedule: EmployeeWeeklySchedule): EmployeeWeeklySchedule {
  const monday = cloneDayEntry(schedule.days.monday);
  const days = { ...schedule.days };
  for (const day of WORK_WEEKDAYS) {
    if (day === 'monday') continue;
    days[day] = cloneDayEntry(monday);
  }
  return { ...schedule, days };
}

export function weekKeyFromDate(date: Date): string {
  return format(startOfWeek(date, { weekStartsOn: WEEK_STARTS_ON }), 'yyyy-MM-dd');
}

export function dateKeyForWeekday(weekKey: string, day: Weekday): string {
  const index = WORK_WEEKDAYS.indexOf(day);
  const monday = new Date(`${weekKey}T12:00:00`);
  return format(addDays(monday, index), 'yyyy-MM-dd');
}

export function getWeekdayFromDate(date: Date): Weekday | null {
  return JS_DAY_TO_WEEKDAY[getDay(date)] ?? null;
}

export function clampToWeekday(date: Date, weekStart: Date): Date {
  if (getWeekdayFromDate(date)) return date;
  return new Date(weekStart);
}

export function formatWeekRangeLabel(weekStart: Date): string {
  const weekEnd = addDays(weekStart, WORK_WEEKDAYS.length - 1);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const start = weekStart.toLocaleDateString(undefined, opts);
  const end = weekEnd.toLocaleDateString(undefined, { ...opts, year: 'numeric' });
  return `${start} – ${end}`;
}

export function formatBiWeekRangeLabel(periodStart: Date): string {
  const secondWeekEnd = addDays(periodStart, WORK_WEEKDAYS.length * 2 - 1);
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const start = periodStart.toLocaleDateString(undefined, opts);
  const end = secondWeekEnd.toLocaleDateString(undefined, { ...opts, year: 'numeric' });
  return `${start} – ${end}`;
}

export function getWeekdayDate(day: Weekday, weekStart: Date): Date {
  return addDays(weekStart, WORK_WEEKDAYS.indexOf(day));
}

function englishOrdinalSuffix(day: number): 'st' | 'nd' | 'rd' | 'th' {
  if (day >= 11 && day <= 13) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

export type DayHeaderDateDisplay =
  | { type: 'superscript'; month: string; day: number; suffix: 'st' | 'nd' | 'rd' | 'th' }
  | { type: 'plain'; text: string };

export function getDayHeaderDateDisplay(date: Date, language: string): DayHeaderDateDisplay {
  const month = format(date, 'MMM');
  const day = date.getDate();

  if (language.startsWith('fr')) {
    return {
      type: 'plain',
      text: day === 1 ? `1er ${month.toLowerCase()}` : `${day} ${month.toLowerCase()}`,
    };
  }
  if (language.startsWith('zh')) {
    return { type: 'plain', text: `${format(date, 'M')}月${day}日` };
  }
  return { type: 'superscript', month, day, suffix: englishOrdinalSuffix(day) };
}

/** Abbreviated month + day number, e.g. "Jun 1st" (en), "1er juin" (fr), "6月1日" (zh). */
export function formatDayHeaderDatePart(date: Date, language: string): string {
  const display = getDayHeaderDateDisplay(date, language);
  if (display.type === 'superscript') {
    return `${display.month} ${display.day}${display.suffix}`;
  }
  return display.text;
}

export function deriveDailyShifts(
  schedules: EmployeeWeeklySchedule[],
  staff: StaffMember[],
  date: Date
): ShiftAssignment[] {
  const weekday = getWeekdayFromDate(date);
  if (!weekday) return [];

  const staffById = new Map(staff.map((s) => [s.id, s]));
  const shifts: ShiftAssignment[] = [];

  for (const schedule of schedules) {
    const entry = schedule.days[weekday];
    if (!entry || entry.isOff) continue;

    const member = staffById.get(schedule.employeeId);
    if (!member) continue;

    const colorClass = member.blockColorClass ?? FALLBACK_BLOCK_COLOR;

    for (const roomId of entry.roomIds) {
      if (!roomId) continue;
      shifts.push({
        id: `${schedule.employeeId}-${roomId}-${weekday}`,
        staffId: schedule.employeeId,
        roomId,
        startTime: entry.start,
        endTime: entry.end,
        colorClass,
      });
    }
  }

  return shifts;
}

export function buildDayEntry(
  start: string,
  end: string,
  lunchStart: string,
  lunchEnd: string,
  roomIds: string[]
): DayShiftEntry {
  return { start, end, lunchStart, lunchEnd, roomIds, isOff: false };
}

export function applyDayFieldUpdate(
  entry: DayShiftEntry | null,
  field: DayShiftField,
  value: string
): DayShiftEntry {
  const base = entry ?? createDefaultDayEntry();
  const next: DayShiftEntry = { ...base, isOff: false, [field]: value };
  // New / updated start → keep default 8.5h paid length (9h clock).
  if (field === 'start' && value.trim()) {
    next.end = defaultEndFromStart(value);
  }
  return next;
}

export function applyDayRoomUpdate(
  entry: DayShiftEntry | null,
  roomId: string
): DayShiftEntry {
  const base = entry ?? createDefaultDayEntry();
  return {
    ...base,
    isOff: false,
    roomIds: roomId ? [roomId] : [],
  };
}

export function applyDayOpeningToggle(
  entry: DayShiftEntry | null,
  enabled: boolean
): DayShiftEntry {
  const base = entry ?? createDefaultDayEntry();
  if (!enabled) {
    return { ...base, isOff: false, isOpening: false };
  }
  return {
    ...base,
    isOff: false,
    isOpening: true,
    start: OPENING_DEFAULT_START,
    end: defaultEndFromStart(OPENING_DEFAULT_START),
  };
}

export function applyDayClosingToggle(
  entry: DayShiftEntry | null,
  enabled: boolean
): DayShiftEntry {
  const base = entry ?? createDefaultDayEntry();
  if (!enabled) {
    return { ...base, isOff: false, isClosing: false };
  }
  return {
    ...base,
    isOff: false,
    isClosing: true,
    start: CLOSING_DEFAULT_START,
    end: CLOSING_DEFAULT_END,
  };
}
